import createDecorator from 'final-form-calculate';
import React from 'react';
import { Field, Form } from 'react-final-form';
import { useSelector } from 'react-redux';

import { useIsMobile } from '@sravni/react-utils';

import { FieldAmountControl } from '@src/components/form/FieldAmountControl';
import { FieldTermControl } from '@src/components/form/FieldTermControl';
import { emptyArray } from '@src/helpers/emptyArray';
import { selectItemSource } from '@src/reducers/product';

import { Condition } from '../Condition';
import { getAmountByTerm, getReturnCondition, getTermByAmount, mapDaysToMinutes, mapMinutesToDays } from '../utils';

import styles from './styles.module.scss';

type FormType = {
  amount: number;
  term: number;
};

export const Calculator: React.FC = () => {
  const isMobile = useIsMobile();

  const { rates: { RUB: rates = emptyArray } = {} } = useSelector(selectItemSource) ?? {};
  const amountMin = Math.min(...rates.map(({ amount }) => amount.from ?? 0));
  const amountMax = Math.max(...rates.map(({ amount }) => amount.to ?? 0));
  const termMin = Math.min(...rates.flatMap(({ periods }) => periods.map(({ term }) => term.fromValue ?? 0)));
  const termMax = Math.max(...rates.flatMap(({ periods }) => periods.map(({ term }) => term.toValue ?? 0)));

  const calculator = createDecorator(
    {
      field: 'amount',
      updates: {
        term: (newAmount, valuesBase) => {
          const { term } = valuesBase as FormType;
          const returnCondition = getReturnCondition(rates, newAmount, term);
          if (returnCondition === undefined) {
            const newTerm = getTermByAmount(rates, newAmount, term);
            if (newTerm !== undefined) {
              return newTerm;
            }
          }
          return term;
        },
      },
    },
    {
      field: 'term',
      updates: {
        amount: (newTerm, valuesBase) => {
          const { amount } = valuesBase as FormType;
          const returnCondition = getReturnCondition(rates, amount, newTerm);
          if (returnCondition === undefined) {
            const newAmount = getAmountByTerm(rates, newTerm, amount);
            if (newAmount !== undefined) {
              return newAmount;
            }
          }
          return amount;
        },
      },
    },
  );

  const initialValue = React.useMemo(
    (): FormType => ({
      amount: amountMin,
      term: getTermByAmount(rates, amountMin, termMin) ?? termMin,
    }),
    [rates, amountMin, termMin],
  );
  return (
    <div className={styles.container}>
      <Form initialValues={initialValue} decorators={[calculator]} onSubmit={() => {}}>
        {() => (
          <div className={styles.calculator_container}>
            <div className={styles.controlWrapper}>
              <h2 className={styles.title}>{isMobile ? 'Расчёт займа' : 'Рассчитать график платежей'}</h2>
              <Field
                name="amount"
                label={`Введите сумму от ${amountMin} до ${amountMax} руб`}
                max={amountMax}
                validate={(amount) => (amount < amountMin ? 'Сумма ниже минимальной' : undefined)}
                className={styles.amount}
                component={FieldAmountControl}
              />
              <Field
                name="term"
                label={`Срок от ${mapMinutesToDays(termMin)} до ${mapMinutesToDays(termMax)} дней`}
                format={mapMinutesToDays}
                parse={mapDaysToMinutes}
                max={mapMinutesToDays(termMax)}
                className={styles.term}
                validate={(term) => (term < termMin ? 'Срок ниже минимального' : undefined)}
                component={FieldTermControl}
              />
              <div className={styles.description}>
                Расчеты в калькуляторе являются предварительными. Подробные условия вы сможете узнать при оформлении
                заявки. Расчет даты возврата производится с текущего дня.
              </div>
            </div>

            <Condition />
          </div>
        )}
      </Form>
    </div>
  );
};
